import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isValid } from 'date-fns';
import { clsx } from 'clsx';
import { WorkstationInfo } from '../services/api';
import { Card, Button, StatusBadge, LoadingPage } from '../components/Layout';
import WorkCardModal from '../components/WorkCardModal';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Safe date formatting helper
const safeFormatDate = (dateStr: string | undefined | null, formatStr: string, fallback = 'N/A'): string => {
  if (!dateStr) return fallback;
  try {
    const date = new Date(dateStr);
    if (!isValid(date)) return fallback;
    return format(date, formatStr);
  } catch {
    return fallback;
  }
};

// Display name mappings for product type and fabric type slugs
const PRODUCT_TYPE_LABELS: Record<string, string> = {
  solar94: 'Solar 94%',
  solar99: 'Solar 99%',
  cvw: 'Clear Vinyl Window',
  insect: 'Insect Screen',
  part: 'Part',
};

const FABRIC_TYPE_LABELS: Record<string, string> = {
  omega_pro_3: 'Omega Pro 3%',
  omega_pro_5: 'Omega Pro 5%',
  omega_pro_10: 'Omega Pro 10%',
  twitchell_80: 'Twitchell 80%',
  twitchell_nano_95: 'Twitchell Nano 95%',
  twitchell_nano_55_insect: 'Twitchell Nano 55 Insect',
  '502_vinyl': '502 Vinyl',
};

function formatProductType(slug: string): string {
  return PRODUCT_TYPE_LABELS[slug] || slug;
}

function formatFabricType(slug: string): string {
  return FABRIC_TYPE_LABELS[slug] || slug;
}

// Normalize API response to match our display needs
interface CutSizes {
  fabricWidth?: number;
  fabricHeight?: number;
  weightBar?: number;
  rollerTube?: number;
  housingMain?: number;
  housingCover?: number;
  vTrack?: number;
  uChannel?: number;
  kederGuide?: number;
  screwCover?: number;
}

interface QueueItem {
  id: string;
  workOrderId: string;
  orderId: string;
  orderNumber: string;
  itemNumber: number;
  productName: string;
  customerName: string;
  sidemark?: string;
  width: number;
  height: number;
  status: string;
  isLate?: boolean;
  dueDate?: string;
  stepName: string;
  screenMaterial?: string;
  screenColor?: string;
  systemColor?: string;
  componentColors?: { [key: string]: string };
  startedAt?: string;
  cutSizes?: CutSizes;
  workstationCode?: string;
  systemWidth?: number;
  systemHeight?: number;
  boxWidth?: number;
  motorSide?: string;
  screwCovers?: boolean;
  uChannel?: string;
  shortageHold?: boolean;
  binColor?: string | null;
  shippingMethod?: string | null;
  partLength?: number | null;
  partQuantity?: number | null;
  partUnit?: string | null;
}

// Stations that show cut sizes (specific part cut lengths)
const CUT_SIZE_STATIONS = ['SAW1', 'SAW2', 'SAW3', 'SAW4', 'CUTTABLE'];
// Stations that show system/box dimensions
const ASSEMBLY_STATIONS = ['TRACK-ASSEMBLY', 'BOX-ASSEMBLY', 'ROLLER-ASSEMBLY', 'WB-ASSEMBLY', 'FINAL-ASSEMBLY', 'QC', 'PACKWC', 'PACKING'];

// Get workstation-relevant specs for display
// valueClass: 'cut' for yellow, 'blue' for blue, 'indigo' for indigo (mapped to CSS classes)
function getStationSpecs(wsCode: string, item: QueueItem): { label: string; value: string; valueClass: string }[] {
  const cs = item.cutSizes;
  const code = (wsCode || '').toUpperCase();
  const specs: { label: string; value: string; valueClass: string }[] = [];

  // Parts orders: show part-specific info
  if (item.productName && item.productName !== 'Keder Screen' && item.partLength) {
    specs.push({ label: 'Cut Length', value: `${item.partLength}"`, valueClass: 'cut' });
    if (item.partQuantity && item.partQuantity > 1) {
      specs.push({ label: 'Qty', value: `${item.partQuantity}`, valueClass: 'blue' });
    }
    return specs;
  }

  if (CUT_SIZE_STATIONS.includes(code)) {
    // Saw stations + cut table: show individual cut sizes
    if (!cs) return [];
    switch (code) {
      case 'SAW1':
        if (cs.housingMain) specs.push({ label: 'Housing', value: `${cs.housingMain}"`, valueClass: 'cut' });
        if (cs.housingCover) specs.push({ label: 'Cover', value: `${cs.housingCover}"`, valueClass: 'cut' });
        break;
      case 'SAW2':
        if (cs.vTrack) specs.push({ label: 'V-Track', value: `${cs.vTrack}"`, valueClass: 'cut' });
        if (cs.uChannel && item.uChannel && item.uChannel !== 'none') specs.push({ label: 'U-Channel', value: `${cs.uChannel}"`, valueClass: 'cut' });
        if (cs.kederGuide) specs.push({ label: 'Keder Guide', value: `${cs.kederGuide}"`, valueClass: 'cut' });
        if (cs.screwCover && item.screwCovers) specs.push({ label: 'Screw Cover', value: `${cs.screwCover}"`, valueClass: 'cut' });
        break;
      case 'SAW3':
        if (cs.rollerTube) specs.push({ label: 'Roller Tube', value: `${cs.rollerTube}"`, valueClass: 'cut' });
        break;
      case 'SAW4':
        if (cs.weightBar) specs.push({ label: 'Weight Bar', value: `${cs.weightBar}"`, valueClass: 'cut' });
        break;
      case 'CUTTABLE':
        if (cs.fabricWidth) specs.push({ label: 'Fabric W', value: `${cs.fabricWidth}"`, valueClass: 'cut' });
        if (cs.fabricHeight) specs.push({ label: 'Fabric H', value: `${cs.fabricHeight}"`, valueClass: 'cut' });
        break;
    }
    return specs;
  }

  // WB-ASSEMBLY: show weight bar cut size (backup if label is lost)
  if (code === 'WB-ASSEMBLY') {
    if (cs?.weightBar) specs.push({ label: 'Weight Bar', value: `${cs.weightBar}"`, valueClass: 'cut' });
    return specs;
  }

  if (ASSEMBLY_STATIONS.includes(code) || code.includes('ASSEMBLY')) {
    // Assembly + QC + packing: show system width, box width, height
    const sysW = item.systemWidth;
    const sysH = item.systemHeight;
    // Box width from specs (set during release), fallback to housingMain + 0.5 (reverse cut formula)
    const boxW = item.boxWidth || (cs?.housingMain ? cs.housingMain + 0.5 : null);

    if (sysW) specs.push({ label: 'System W', value: `${sysW}"`, valueClass: 'blue' });
    if (boxW && code !== 'TRACK-ASSEMBLY') specs.push({ label: 'Box W', value: `${Number(boxW).toFixed(4).replace(/\.?0+$/, '')}"`, valueClass: 'indigo' });
    if (sysH) specs.push({ label: 'Height', value: `${sysH}"`, valueClass: 'blue' });
    // Motor side for housing and roller assembly
    if (item.motorSide && (code === 'BOX-ASSEMBLY' || code === 'ROLLER-ASSEMBLY')) {
      specs.push({ label: 'Motor', value: item.motorSide === 'left' ? 'Left' : 'Right', valueClass: 'blue' });
    }
    return specs;
  }

  // Other stations: show fabric dimensions if available
  if (cs) {
    if (cs.fabricWidth) specs.push({ label: 'Fabric W', value: `${cs.fabricWidth}"`, valueClass: 'cut' });
    if (cs.fabricHeight) specs.push({ label: 'Fabric H', value: `${cs.fabricHeight}"`, valueClass: 'cut' });
  }
  return specs;
}

function normalizeQueueItem(raw: any): QueueItem {
  return {
    id: raw.id,
    workOrderId: raw.workOrderId || raw.id,
    orderId: raw.orderId,
    orderNumber: raw.orderNumber || raw.specs?.orderNumber || '',
    itemNumber: raw.itemNumber || raw.specs?.itemNumber || 1,
    productName: raw.specs?.partName || raw.productName || (raw.productType === 'part' ? 'Part' : formatProductType(raw.productType || '')) || raw.specs?.productType || 'Keder Screen',
    customerName: raw.customerName || raw.companyName || '',
    sidemark: raw.sidemark || raw.specs?.sidemark || '',
    width: raw.width || raw.maxWidth || raw.specs?.width || 0,
    height: raw.height || raw.maxHeight || raw.specs?.height || 0,
    status: raw.status,
    isLate: raw.isLate,
    dueDate: raw.dueDate || raw.neededByDate,
    stepName: raw.stepName || raw.workstationName || '',
    screenMaterial: formatFabricType(raw.screenMaterial || raw.fabricType || raw.specs?.fabricType || ''),
    screenColor: raw.screenColor || raw.fabricColor || raw.specs?.fabricColor || '',
    systemColor: raw.systemColor || raw.specs?.systemColor || '',
    componentColors: raw.componentColors || raw.specs?.componentColors || null,
    startedAt: raw.startedAt,
    cutSizes: raw.cutSizes || null,
    workstationCode: raw.workstationCode || '',
    systemWidth: raw.specs?.systemWidth || null,
    systemHeight: raw.specs?.systemHeight || null,
    boxWidth: raw.specs?.boxWidth || null,
    motorSide: raw.motorSide || raw.specs?.motorSide || null,
    screwCovers: raw.screwCovers ?? false,
    uChannel: raw.uChannel || 'none',
    shortageHold: raw.shortageHold || false,
    binColor: raw.binColor || null,
    shippingMethod: raw.shippingMethod || null,
    partLength: raw.specs?.partLength || null,
    partQuantity: raw.specs?.partQuantity || null,
    partUnit: raw.specs?.partUnit || null,
  };
}

interface StationQueue {
  workstation: WorkstationInfo;
  items: QueueItem[];
}

export function MyQueuePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stationQueues, setStationQueues] = useState<StationQueue[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedStation, setSelectedStation] = useState<string>('all');
  const [expandedStations, setExpandedStations] = useState<Record<string, boolean>>({});
  const [showStationOverview, setShowStationOverview] = useState(false);
  // Batch-work filters: lets a cutter grab "all black extrusions" or
  // "all Omega Pro 5% Black" across the day's queue in one pass.
  const [extrusionColorFilter, setExtrusionColorFilter] = useState<string>('all');
  const [fabricFilter, setFabricFilter] = useState<string>('all');

  // Work-card modal — opens in-place instead of navigating away to /work-cards.
  // Back button on the queue used to send workers back to the dashboard.
  const [cardModal, setCardModal] = useState<{ open: boolean; wsId: string; woId: string }>({
    open: false, wsId: '', woId: '',
  });
  const openCard = (wsId: string, woId: string) => setCardModal({ open: true, wsId, woId });
  const closeCard = () => {
    setCardModal({ open: false, wsId: '', woId: '' });
    // Refresh queue so completed cards drop off / new-ready ones surface.
    loadAllQueues();
  };

  const loadAllQueues = useCallback(async () => {
    try {
      const response = await api.getWorkstations();
      if (!Array.isArray(response) || response.length === 0) {
        setStationQueues([]);
        return;
      }

      // Filter workstations by user's assigned workstation_types
      const userWorkstationTypes = (user as any)?.permissions?.workstation_types || [];
      const hasAllAccess = (user as any)?.permissions?.all_access || userWorkstationTypes.length === 0;

      let assignedWorkstations = response;
      if (!hasAllAccess && userWorkstationTypes.length > 0) {
        assignedWorkstations = response.filter((ws: any) => {
          const wsId = (ws.id || '').toLowerCase();
          const wsCode = (ws.code || '').toLowerCase();
          const wsName = (ws.name || '').toLowerCase();
          return userWorkstationTypes.some((type: string) => {
            const typeLower = type.toLowerCase();
            return wsId === typeLower || wsCode === typeLower ||
                   wsCode.includes(typeLower) || typeLower.includes(wsCode) ||
                   wsName.includes(typeLower);
          });
        });
      }

      // Fetch queue for each assigned workstation in parallel
      const queues = await Promise.all(
        assignedWorkstations.map(async (ws: WorkstationInfo) => {
          try {
            const queueData = await api.getMyQueue(ws.id);
            const items = Array.isArray(queueData)
              ? queueData.map(normalizeQueueItem).filter(
                  (item) => item.status === 'in_progress' || item.status === 'ready_to_start' || item.status === 'ready'
                )
              : [];
            return { workstation: ws, items };
          } catch {
            return { workstation: ws, items: [] };
          }
        })
      );

      // Only show stations that have items, but always show at least the assigned ones
      setStationQueues(queues);
    } catch (error) {
      console.error('Failed to load queues:', error);
      setStationQueues([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadAllQueues();
  }, [loadAllQueues]);

  /**
   * "Start <station>" — actually START the work order, then open the card.
   *
   * This button used to call openCard() only: it was labelled Start, styled as
   * the primary action, and never called the start endpoint. So `started_at`
   * stayed NULL while the operator worked, and completeAllSteps'
   * `started_at = COALESCE(started_at, NOW())` then stamped the START at the
   * moment of COMPLETION — every work order recorded ~0.03s of labor and the
   * Labor BOM showed "—" / $0.00 across the board. Confirmed by a controlled
   * test (VT26070013 SAW3): a deliberate 45-second wait recorded as 0.05s.
   *
   * Start failures (e.g. unmet dependencies -> "Cannot start — waiting on: …")
   * are surfaced instead of silently opening a card the operator can't work.
   */
  const handleStartAndOpen = async (workstationId: string, workOrderId: string) => {
    setActionLoading(workOrderId);
    try {
      await api.startWorkOrder(workOrderId);
    } catch (error: any) {
      console.error('Failed to start work order:', error);
      const msg = error?.response?.data?.message || 'Could not start this work order';
      alert(msg);
      setActionLoading(null);
      return;
    }
    setActionLoading(null);
    openCard(workstationId, workOrderId);
  };

  const handleAction = async (action: 'start' | 'complete' | 'skip', itemId: string) => {
    setActionLoading(itemId);
    try {
      if (action === 'start') await api.startWorkOrder(itemId);
      else if (action === 'complete') await api.completeWorkOrder(itemId);
      else await api.skipWorkOrder(itemId, 'Skipped from queue');
      await loadAllQueues();
    } catch (error) {
      console.error(`Failed to ${action} work:`, error);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  // Normalizers for filter compare (color/material slugs are inconsistent
  // — sometimes "Black", sometimes "black", sometimes "BLACK").
  const normColor = (v: string | undefined | null) =>
    String(v || '').trim().toLowerCase().replace(/[_-]+/g, ' ');
  const fabricKey = (item: QueueItem) => {
    const m = normColor(item.screenMaterial);
    const c = normColor(item.screenColor);
    if (!m && !c) return '';
    return `${m}|${c}`;
  };
  const fabricLabel = (key: string) => {
    if (!key) return '';
    const [m, c] = key.split('|');
    const cap = (s: string) => s.replace(/\b\w/g, ch => ch.toUpperCase());
    return `${cap(m || '—')}${c ? ` — ${cap(c)}` : ''}`;
  };

  // Distinct filter options across every station's items so a worker can
  // batch by "all black extrusions" or "all Omega Pro 5% Black" today.
  const extrusionColors = Array.from(
    new Set(
      stationQueues.flatMap(sq => sq.items.map(i => normColor(i.systemColor))).filter(Boolean),
    ),
  ).sort();
  const fabricOptions = Array.from(
    new Set(stationQueues.flatMap(sq => sq.items.map(fabricKey)).filter(Boolean)),
  ).sort();

  const itemMatchesFilters = (item: QueueItem) => {
    if (extrusionColorFilter !== 'all' && normColor(item.systemColor) !== extrusionColorFilter) return false;
    if (fabricFilter !== 'all' && fabricKey(item) !== fabricFilter) return false;
    return true;
  };

  const totalInProgress = stationQueues.reduce((sum, sq) => sum + sq.items.filter(i => itemMatchesFilters(i) && i.status === 'in_progress').length, 0);
  const totalReady = stationQueues.reduce((sum, sq) => sum + sq.items.filter(i => itemMatchesFilters(i) && (i.status === 'ready_to_start' || i.status === 'ready')).length, 0);
  const stationsWithItems = stationQueues.filter(sq => sq.items.some(itemMatchesFilters));
  const filterActive = extrusionColorFilter !== 'all' || fabricFilter !== 'all';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">My Queue</h1>
        <button
          onClick={() => { setLoading(true); loadAllQueues(); }}
          className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="text-center">
          <div className="text-2xl font-bold text-yellow-400">{totalInProgress}</div>
          <div className="text-xs text-gray-400">In Progress</div>
        </Card>
        <Card className="text-center">
          <div className="text-2xl font-bold text-white">{totalReady}</div>
          <div className="text-xs text-gray-400">Ready</div>
        </Card>
        <Card className="text-center">
          <div className="text-2xl font-bold text-gray-500">{stationQueues.length}</div>
          <div className="text-xs text-gray-400">Stations</div>
        </Card>
      </div>

      {/* Batch filters: extrusion color + fabric */}
      {(extrusionColors.length > 0 || fabricOptions.length > 0) && (
        <div className="flex flex-wrap gap-2 items-center">
          {extrusionColors.length > 0 && (
            <div className="flex-1 min-w-[140px]">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Extrusion Color</label>
              <select
                value={extrusionColorFilter}
                onChange={e => setExtrusionColorFilter(e.target.value)}
                className="w-full px-2 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white [color-scheme:dark]"
              >
                <option value="all">All colors</option>
                {extrusionColors.map(c => (
                  <option key={c} value={c}>{c.replace(/\b\w/g, ch => ch.toUpperCase())}</option>
                ))}
              </select>
            </div>
          )}
          {fabricOptions.length > 0 && (
            <div className="flex-1 min-w-[180px]">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Fabric</label>
              <select
                value={fabricFilter}
                onChange={e => setFabricFilter(e.target.value)}
                className="w-full px-2 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white [color-scheme:dark]"
              >
                <option value="all">All fabrics</option>
                {fabricOptions.map(f => (
                  <option key={f} value={f}>{fabricLabel(f)}</option>
                ))}
              </select>
            </div>
          )}
          {filterActive && (
            <button
              onClick={() => { setExtrusionColorFilter('all'); setFabricFilter('all'); }}
              className="self-end px-3 py-2 text-xs text-yellow-400 hover:text-yellow-300 font-medium whitespace-nowrap"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Station Filter - Collapsible */}
      {stationQueues.length > 0 && (
        <div>
          <button
            onClick={() => setShowStationOverview(prev => !prev)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all active:scale-[0.98]"
            style={{ background: '#1e293b', border: '1px solid #334155' }}
          >
            <span className="text-xs font-bold" style={{ color: '#f8fafc' }}>Stations</span>
            <span
              className="px-2 py-0.5 text-xs font-bold rounded-full"
              style={{ background: 'linear-gradient(135deg, #facc15, #eab308)', color: '#0f172a' }}
            >
              {totalInProgress + totalReady}
            </span>
            <svg
              className="w-3.5 h-3.5 transition-transform duration-200"
              style={{ color: '#94a3b8', transform: showStationOverview ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showStationOverview && (
            <div className="grid grid-cols-4 gap-2 mt-2">
              <button
                onClick={() => setSelectedStation('all')}
                className={`px-2 py-2 rounded-lg text-xs font-bold text-center transition-colors ${
                  selectedStation === 'all'
                    ? 'bg-yellow-500 text-black'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                All ({totalInProgress + totalReady})
              </button>
              {stationQueues.map(({ workstation, items }) => {
                const visible = items.filter(itemMatchesFilters).length;
                return (
                  <button
                    key={workstation.id}
                    onClick={() => setSelectedStation(workstation.id)}
                    className={`px-2 py-2 rounded-lg text-xs font-bold text-center transition-colors ${
                      selectedStation === workstation.id
                        ? 'bg-yellow-500 text-black'
                        : visible === 0
                          ? 'bg-gray-800 text-gray-500'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {workstation.name.replace(/-/g, ' ').replace('ASSEMBLY', 'ASSY')} ({visible})
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Workstation Groups */}
      {(() => {
        const filtered = selectedStation === 'all'
          ? stationsWithItems
          : stationsWithItems.filter(sq => sq.workstation.id === selectedStation);
        return filtered.length > 0 ? (
        filtered.map(({ workstation, items }) => {
          const visibleItems = items.filter(itemMatchesFilters);
          const wsInProgress = visibleItems.filter(i => i.status === 'in_progress');
          const wsReady = visibleItems.filter(i => i.status === 'ready_to_start' || i.status === 'ready');
          const isExpanded = expandedStations[workstation.id] ?? false;

          return (
            <div key={workstation.id}>
              {/* Workstation Header - Clickable */}
              <button
                onClick={() => setExpandedStations(prev => ({ ...prev, [workstation.id]: !prev[workstation.id] }))}
                className="w-full mt-3 flex items-center justify-between px-4 py-3.5 rounded-xl transition-all active:scale-[0.98]"
                style={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                  border: '1px solid #334155',
                  boxShadow: isExpanded
                    ? '0 0 12px rgba(250,204,21,0.15), inset 0 1px 0 rgba(255,255,255,0.05)'
                    : 'inset 0 1px 0 rgba(255,255,255,0.05)',
                  borderLeft: wsInProgress.length > 0 ? '3px solid #22c55e' : '3px solid #facc15',
                }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <h2 className="text-sm font-bold uppercase tracking-wider whitespace-nowrap truncate" style={{ color: '#f8fafc' }}>
                    {workstation.name}
                  </h2>
                  {wsInProgress.length > 0 && (
                    <span
                      className="px-2 py-0.5 text-xs font-bold rounded-full animate-pulse flex-shrink-0"
                      style={{ background: 'rgba(34,197,94,0.2)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }}
                    >
                      {wsInProgress.length} active
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2.5 flex-shrink-0">
                  <span
                    className="text-xs font-bold rounded-full min-w-[26px] h-[26px] flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #facc15, #eab308)', color: '#0f172a', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}
                  >
                    {items.length}
                  </span>
                  <svg
                    className="w-4 h-4 transition-transform duration-200"
                    style={{ color: '#94a3b8', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {/* Collapsible content */}
              {isExpanded && (
                <div className="space-y-3">
                  {/* In Progress at this station */}
                  {wsInProgress.map((item, idx) => (
                    <div key={item.id}>
                      {idx > 0 && <div className="h-0.5 bg-yellow-500/40 my-3 rounded-full" />}
                      <WorkItemCard
                        item={item}
                        onComplete={() => handleAction('complete', item.id)}
                        onSkip={() => handleAction('skip', item.id)}
                        onViewDetails={() => navigate(`/work/${item.id}`)}
                        loading={actionLoading === item.id}
                        stationName={workstation.name}
                        stationCode={(workstation as any).code || ''}
                      />
                    </div>
                  ))}

                  {/* Divider between in-progress and ready sections */}
                  {wsInProgress.length > 0 && wsReady.length > 0 && (
                    <div className="h-0.5 bg-yellow-500/40 my-3 rounded-full" />
                  )}

                  {/* Ready at this station */}
                  {wsReady.map((item, idx) => (
                    <div key={item.id}>
                      {idx > 0 && <div className="h-0.5 bg-yellow-500/40 my-3 rounded-full" />}
                      <WorkItemCard
                        item={item}
                        onStart={() => handleStartAndOpen(workstation.id, item.id)}
                        onSkip={() => handleAction('skip', item.id)}
                        onViewDetails={() => navigate(`/work/${item.id}`)}
                        loading={actionLoading === item.id}
                        stationName={workstation.name}
                        stationCode={(workstation as any).code || ''}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      ) : (
        <Card className="text-center py-12">
          <div className="text-gray-400 text-lg">No items in queue</div>
          <div className="text-gray-500 text-sm mt-2">
            {stationQueues.length > 0
              ? `Monitoring ${stationQueues.length} assigned station${stationQueues.length !== 1 ? 's' : ''} - check back later`
              : 'No workstations assigned to your profile'}
          </div>
        </Card>
      );
      })()}

      <WorkCardModal
        open={cardModal.open}
        workstationId={cardModal.wsId}
        workOrderId={cardModal.woId}
        onClose={closeCard}
      />
    </div>
  );
}

interface WorkItemCardProps {
  item: QueueItem;
  onStart?: () => void;
  onComplete?: () => void;
  onSkip?: () => void;
  onViewDetails?: () => void;
  loading?: boolean;
  stationName?: string;
  stationCode?: string;
}

function WorkItemCard({ item, onStart, onComplete, onSkip, onViewDetails, loading, stationName, stationCode }: WorkItemCardProps) {
  const isReady = item.status === 'ready_to_start' || item.status === 'ready';

  return (
    <Card className={clsx(
      'border-l-4',
      item.status === 'in_progress' ? 'border-l-yellow-400' : 'border-l-gray-600',
      item.isLate && 'border-l-red-500'
    )}>
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-yellow-400 font-semibold">{item.orderNumber}</span>
            {item.binColor && (
              <span
                className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${
                  item.binColor === 'red' ? 'bg-red-500 animate-pulse' : 'bg-gray-500'
                }`}
                title={item.binColor === 'red' ? 'Red bin — parts shortage' : 'Black bin — parts ready'}
              />
            )}
            {item.isLate && (
              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded">LATE</span>
            )}
          </div>
          <div className="text-sm text-gray-400">Item {item.itemNumber} &bull; {item.stepName}</div>
        </div>
        <StatusBadge status={item.status === 'ready' ? 'ready_to_start' : item.status} />
      </div>

      {/* Product Info */}
      <div className="mb-3">
        <div className="text-sm text-gray-400 flex items-center gap-2">
          <span>{item.customerName}{item.sidemark ? ` \u2022 ${item.sidemark}` : ''}</span>
          {item.shippingMethod && (
            <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${
              item.shippingMethod === 'will_call' || item.shippingMethod === 'willcall'
                ? 'bg-blue-500/20 text-blue-400'
                : 'bg-purple-500/20 text-purple-400'
            }`}>
              {item.shippingMethod === 'will_call' || item.shippingMethod === 'willcall' ? 'WILL CALL' : 'SHIP'}
            </span>
          )}
        </div>
      </div>

      {/* Station-specific specs */}
      {(() => {
        const code = stationCode || item.workstationCode || '';
        const stationSpecs = getStationSpecs(code, item);
        const isCutStation = CUT_SIZE_STATIONS.includes(code.toUpperCase());
        const isAssemblyStation = ASSEMBLY_STATIONS.includes(code.toUpperCase()) || code.toUpperCase().includes('ASSEMBLY');

        if (stationSpecs.length > 0) {
          const boxClass = isCutStation ? 'spec-box-cut' : 'spec-box-system';
          const headerText = isCutStation ? 'CUT SIZES' : isAssemblyStation ? 'SYSTEM DIMENSIONS' : 'DIMENSIONS';

          return (
            <div className={`mb-3 p-2 ${boxClass} border rounded`}>
              <div className="text-xs spec-header mb-1 font-semibold">{headerText}</div>
              <div className={`grid ${stationSpecs.length > 1 ? 'grid-cols-2' : 'grid-cols-1'} gap-2 text-sm`}>
                {stationSpecs.map(spec => (
                  <div key={spec.label}>
                    <span className="spec-label">{spec.label}:</span>
                    <span className={`spec-value${spec.valueClass !== 'cut' ? `-${spec.valueClass}` : ''} ml-2 font-bold font-mono`}>{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        if (item.width > 0 || item.height > 0) {
          return (
            <div className="mb-3 p-2 spec-box-system border rounded">
              <div className="text-xs spec-header mb-1 font-semibold">UNIT DIMENSIONS</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="spec-label">Width:</span>
                  <span className="spec-value-blue ml-2 font-bold">{item.width}"</span>
                </div>
                <div>
                  <span className="spec-label">Height:</span>
                  <span className="spec-value-blue ml-2 font-bold">{item.height}"</span>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Due Date */}
      {item.dueDate && (
        <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
          <div>
            <span className="text-gray-500">Due:</span>
            <span className={clsx('ml-2', item.isLate ? 'text-red-400' : 'text-white')}>
              {safeFormatDate(item.dueDate, 'MMM d')}
            </span>
          </div>
        </div>
      )}

      {/* Material Info */}
      {(item.screenMaterial || item.screenColor || item.systemColor) && (
        <div className="text-sm mb-4 space-y-1">
          <div>
            {item.screenMaterial && (
              <>
                <span className="text-gray-500">Material:</span>
                <span className="text-white ml-2">{item.screenMaterial}</span>
              </>
            )}
            {item.screenColor && (
              <>
                <span className="text-gray-500 ml-3">Color:</span>
                <span className="text-white ml-2">{item.screenColor}</span>
              </>
            )}
          </div>
          {item.systemColor && (
            <div>
              <span className="text-gray-500">Frame:</span>
              <span className="text-white ml-2">{item.systemColor.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
              {item.componentColors && Object.keys(item.componentColors).length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-orange-500 text-white rounded">⚠ MIXED COLORS</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        {isReady && onStart && (
          <Button variant="success" onClick={onStart} disabled={loading} className="flex-1">
            {loading ? 'Starting...' : `Start ${stationName || item.stepName}`}
          </Button>
        )}
        {item.status === 'in_progress' && onComplete && (
          <Button variant="success" onClick={onComplete} disabled={loading} className="flex-1">
            {loading ? 'Completing...' : 'Complete'}
          </Button>
        )}
        {onViewDetails && (
          <Button variant="secondary" onClick={onViewDetails}>
            Details
          </Button>
        )}
        {onSkip && (
          <Button variant="danger" onClick={onSkip} disabled={loading}>
            Skip
          </Button>
        )}
      </div>
    </Card>
  );
}
